/**
 * WarningNotice: the amber inline warning, glyph plus words. Used for the
 * store SKU held count (some store SKUs will be held with a print issue until
 * their per-size file is uploaded), shared by the Store SKUs turn-on dialog
 * (Settings) and the store SKU set-up review (product sync), so the two
 * promises read the same way, and for the set-up review's "the switch changed"
 * note. It lives in `components/shared/` because it serves two features;
 * neither owns it (DESIGN.md section 12).
 *
 * A warning, never a block: no caller gates an action on it. The message is
 * the caller's, already translated in its own namespace. The glyph and the
 * words carry the meaning, so colour is never the only signal. `role` passes
 * through (for example `status`, so a screen reader announces a note that
 * appears while the page is open).
 *
 * `actions` (optional) are the warning's own one-click answers (buttons),
 * placed after the words and wrapping below them on a narrow row. A notice
 * with actions renders as a `div` (buttons never sit inside a `p`), and its
 * caller should not give it `role="status"`: a live region that holds
 * controls re-announces them. Announce the outcome separately instead.
 */
import type { AriaRole, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface WarningNoticeProps {
  children: ReactNode;
  className?: string;
  role?: AriaRole;
  /** The warning's own buttons, after the words (wrap below them when narrow). */
  actions?: ReactNode;
  'data-testid'?: string;
}

const BOX = 'rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900';
const GLYPH = 'mt-0.5 h-4 w-4 shrink-0 text-amber-700';

export function WarningNotice({ children, className, role, actions, 'data-testid': testId }: WarningNoticeProps) {
  if (actions) {
    return (
      <div role={role} className={cn('flex flex-wrap items-center gap-x-3 gap-y-2', BOX, className)} data-testid={testId}>
        <div className="flex min-w-0 flex-1 basis-64 items-start gap-2">
          <AlertTriangle className={GLYPH} aria-hidden="true" />
          <div className="min-w-0">{children}</div>
        </div>
        <div className="flex flex-wrap gap-2">{actions}</div>
      </div>
    );
  }
  return (
    <div role={role} className={cn('flex items-start gap-2', BOX, className)} data-testid={testId}>
      <AlertTriangle className={GLYPH} aria-hidden="true" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}
